import { ensureRouteData } from '@/app/query-client'
import {
  latestNewsletterQueryOptions,
  newsletterArchiveQueryOptions,
} from '@/features/newsletter/api/queries'
import { NEWSLETTER_PAGE_SIZE } from '@/features/newsletter/api/newsletterService'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The `/newsletters` loader.
 *
 * MAIN, both awaited: the hero (`latest`) and the archive's first page —
 * neither is a card the page can draw without; a genuine failure of either
 * (the archive fetch itself, or `latest` for anything other than "nothing
 * published yet") blanks the whole page today, and the route's error page
 * preserves that. `page`/`pageSize` are plain component state, not URL
 * search params (there is no `?page=` on this route), so this loader has no
 * search to read — it only ever needs page one, the same values the screen's
 * own `useState` seeds.
 *
 * Both factories resolve `NEWSLETTER_NOT_FOUND` to `null`/an empty page
 * rather than throwing where that is what the code means (see
 * `latestNewsletterQueryOptions`'s own doc), so `Promise.all` here does not
 * need to pick that case apart from a real failure — only a real failure
 * ever rejects.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own — see the other loaders in this wave for the full reasoning.
 */
export async function loadNewsletters({
  context: { queryClient },
}: {
  context: RouterContext
}): Promise<void> {
  await Promise.all([
    ensureRouteData(queryClient, latestNewsletterQueryOptions()),
    ensureRouteData(queryClient, newsletterArchiveQueryOptions(1, NEWSLETTER_PAGE_SIZE)),
  ])
}
